import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Text } from "components/text";
import { useManuscriptActions } from "hooks/useManuscriptActions";
import { toast } from "utils/toast";
import { useManuscripts } from "../hooks";
import { ManuscriptCard } from "./ManuscriptCard";

export const ManuscriptList = () => {
  const { t } = useTranslation(["uploadManuscript"]);
  const { manuscripts, loading, error } = useManuscripts();
  const [showAllManuscripts, setShowAllManuscripts] = useState(false);
  const { deleteProductWithManuscript } = useManuscriptActions();

  if (loading) {
    return <div className="animate-pulse h-32 bg-secondary-100 rounded-md" />;
  }

  if (error) {
    return (
      <div className="p-4 border border-system-error/20 bg-system-error/5 rounded-md">
        <Text variant="error">
          {t("uploadManuscript:manuscripts.errors.loadingFailed")}
        </Text>
      </div>
    );
  }

  const hasMoreManuscripts = manuscripts.length > 3;

  const handleDelete = async (product: { id: string }) => {
    try {
      await deleteProductWithManuscript(product.id);
      toast.success(t("uploadManuscript:manuscripts.deleteSuccess"));
    } catch {
      toast.error(t("uploadManuscript:manuscripts.deleteError"));
    }
  };

  return (
    <>
      <div className="space-y-2">
        <div className="grid grid-cols-1 gap-4">
          {manuscripts.slice(0, 3).map((manuscript) => (
            <ManuscriptCard
              key={manuscript.id}
              product={manuscript}
              onDelete={handleDelete}
            />
          ))}
        </div>

        {hasMoreManuscripts && (
          <Button
            variant="ghost"
            size="small"
            className="text-primary-600 text-sm"
            label={t("uploadManuscript:manuscripts.seeAll")}
            onClick={() => setShowAllManuscripts(true)}
            icon="arrow-right"
            iconPosition="right"
          />
        )}
      </div>

      <Dialog open={showAllManuscripts} onOpenChange={setShowAllManuscripts}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("uploadManuscript:manuscripts.allManuscripts")}
            </DialogTitle>
            <DialogDescription>
              {t("uploadManuscript:manuscripts.allManuscriptsDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[400px] grid grid-cols-1 gap-3 bg-secondary-25 overflow-y-auto p-3 rounded-2xl">
            {manuscripts.map((manuscript) => (
              <ManuscriptCard
                key={manuscript.id}
                product={manuscript}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
